import { useState } from 'react';

import { cn } from '@/shared/ui/cn';
import { Icon } from '@/shared/ui/Icon';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const BOX: Record<AvatarSize, string> = {
  xs: 'size-[22px] text-[9.5px]',
  sm: 'size-8 text-[11.5px]',
  md: 'size-10 text-[13px]',
  lg: 'size-14 text-[18px]',
  xl: 'size-20 text-[25px]',
  xxl: 'size-[120px] text-[36px]',
};

const ICON: Record<AvatarSize, number> = { xs: 11, sm: 16, md: 20, lg: 28, xl: 40, xxl: 56 };

export type AvatarProps = {
  /** Nom complet : les initiales sont dérivées ici, jamais dans l'écran appelant. */
  name?: string | null;
  /** `shop` utilise l'encre de marque, `person` le gris. Deux traitements, pas six. */
  kind?: 'person' | 'shop';
  src?: string | null;
  size?: AvatarSize;
  /** Anneau or : boutique certifiée BUL Verified. */
  verified?: boolean;
  /** Anneau du direct : la boutique est en direct en ce moment. */
  live?: boolean;
  className?: string;
};

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * Toujours rond. Gris pour une personne, encre de marque pour une boutique.
 * Aucune couleur attribuée par hachage du nom : ce serait six teintes de plus.
 * Une photo qui ne charge pas retombe sur les initiales, jamais sur une icône
 * d'image cassée.
 */
export function Avatar({
  name,
  kind = 'person',
  src,
  size = 'md',
  verified = false,
  live = false,
  className,
}: AvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const shop = kind === 'shop';
  const showImage = Boolean(src) && failed !== src;

  return (
    <span
      className={cn(
        'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-pill font-medium tracking-[-0.02em] select-none',
        BOX[size],
        shop ? 'bg-brand-ink text-fg-on-dark' : 'bg-fill text-fg-strong',
        verified && 'ring-[1.5px] ring-verified-ring ring-offset-[1.5px] ring-offset-surface',
        live && 'ring-2 ring-danger ring-offset-2 ring-offset-surface',
        className,
      )}
      aria-hidden={name ? undefined : true}
      title={name ?? undefined}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(src ?? null)}
          className="size-full object-cover"
        />
      ) : name ? (
        <span aria-hidden>{initials(name)}</span>
      ) : (
        <Icon name="user" size={ICON[size]} className={shop ? 'text-fg-on-dark' : 'text-fg-muted'} />
      )}
    </span>
  );
}
